export type AgentStyle = "desk" | "pebbles" | "pebbles-ring" | "drafting";
export type PersonalPreferences = {
  measurementUnit: "mm" | "cm" | "m";
  openingView: "fit" | "100";
  drawingColor: string;
  agentStyle: AgentStyle;
};

export const defaultPreferences: PersonalPreferences = {
  measurementUnit: "m",
  openingView: "fit",
  drawingColor: "#d65331",
  agentStyle: "desk",
};
export const drawingColorChoices = [
  ["#d65331", "Terracotta"],
  ["#25343b", "Charcoal"],
  ["#517691", "Blue"],
  ["#657a4c", "Green"],
  ["#9b6589", "Violet"],
] as const;
export const agentStyleChoices = [
  ["desk", "Desk friends"],
  ["pebbles", "Pebbles"],
  ["pebbles-ring", "Pebbles with rings"],
  ["drafting", "Drafting friends"],
] as const satisfies readonly (readonly [AgentStyle, string])[];

function key(userId: string) {
  return `calque:preferences:${userId}`;
}

export function getPreferences(userId: string): PersonalPreferences {
  try {
    const saved = JSON.parse(localStorage.getItem(key(userId)) || "null") as Partial<PersonalPreferences> | null;
    return {
      measurementUnit: saved?.measurementUnit === "mm" || saved?.measurementUnit === "cm" ? saved.measurementUnit : "m",
      openingView: saved?.openingView === "100" ? "100" : "fit",
      drawingColor: typeof saved?.drawingColor === "string" &&
        drawingColorChoices.some(([color]) => color === saved.drawingColor!.toLowerCase())
        ? saved.drawingColor.toLowerCase() : defaultPreferences.drawingColor,
      agentStyle: agentStyleChoices.find(([style]) => style === saved?.agentStyle)?.[0] ?? defaultPreferences.agentStyle,
    };
  } catch {
    return { ...defaultPreferences };
  }
}

export function savePreferences(userId: string, patch: Partial<PersonalPreferences>) {
  const next = { ...getPreferences(userId), ...patch };
  if (!drawingColorChoices.some(([color]) => color === next.drawingColor))
    next.drawingColor = defaultPreferences.drawingColor;
  if (!agentStyleChoices.some(([style]) => style === next.agentStyle))
    next.agentStyle = defaultPreferences.agentStyle;
  localStorage.setItem(key(userId), JSON.stringify(next));
  window.dispatchEvent(new CustomEvent("calque:preferences", { detail: { userId, value: next } }));
  return next;
}

export function subscribePreferences(userId: string, listener: (value: PersonalPreferences) => void) {
  const update = (event: Event) => {
    const detail = (event as CustomEvent<{ userId: string; value: PersonalPreferences }>).detail;
    if (detail?.userId === userId) listener(detail.value);
  };
  window.addEventListener("calque:preferences", update);
  return () => window.removeEventListener("calque:preferences", update);
}
